<template>
  <ed-modal
    title="Receipt Folder"
    :show="show"
    @close="handleClose"
    primary-button="ADD"
    :primary-button-disabled="!selectedReceiptId || isProcessing"
    secondary-button="CANCEL"
    @confirm="handleAdd"
    type="medium"
  >
    <!-- PRODUCTDEV-5802 — `loading` is the global old-UI spinner+overlay (main.scss:134).
         The ADD click kicks off a multi-second claim/fetch/re-upload in the parent
         (isProcessing window); previously the only feedback was the disabled ADD button. -->
    <div slot="body" class="receipt-folder-picker-body" :class="{ loading: isProcessing }">
      <p class="picker-subtitle">Select a receipt to attach to this transaction.</p>

      <div v-if="!featureFlags.receiptScanPrefill" class="feature-disabled">
        <p>Receipt folder is not available.</p>
      </div>

      <div v-else-if="isLoading" class="loading-state">
        <p>Loading receipts...</p>
      </div>

      <div v-else-if="!receipts || !receipts.length" class="empty-state">
        <p>No receipts in your folder. Upload a receipt without picking a transaction to add it here.</p>
      </div>

      <div v-else class="receipt-picker">
        <strong class="receipt-label">Receipt</strong>
        <div class="receipt-dropdown" v-click-outside="closePanel">
          <button type="button"
                  class="receipt-dropdown-trigger"
                  :class="{ 'is-expanded': isExpanded }"
                  @click="togglePanel"
                  aria-haspopup="listbox"
                  :aria-expanded="isExpanded ? 'true' : 'false'">
            <template v-if="selectedReceipt">
              <div class="receipt-thumbnail receipt-thumbnail-trigger">
                <i class="icon fas fa-receipt"></i>
              </div>
              <div class="receipt-meta receipt-meta-trigger">
                <div class="merchant">{{ displayMerchant(selectedReceipt) }}</div>
                <div class="receipt-date">{{ formatDate(displayDate(selectedReceipt)) }}</div>
              </div>
              <div class="amount amount-trigger">
                <v-money v-if="selectedReceipt.totalAmount != null">{{ selectedReceipt.totalAmount }}</v-money>
                <span v-else>--</span>
              </div>
            </template>
            <span v-else class="receipt-dropdown-placeholder">Select a receipt...</span>
            <i class="fas fa-chevron-down receipt-dropdown-caret"></i>
          </button>

          <div v-if="isExpanded" class="receipt-dropdown-panel" role="listbox">
            <div
              v-for="receipt in receipts"
              :key="receipt.receiptScanId"
              class="receipt-row"
              :class="{ 'is-selected': selectedReceiptId === receipt.receiptScanId }"
              role="option"
              :aria-selected="selectedReceiptId === receipt.receiptScanId ? 'true' : 'false'"
              @click="onRowClick(receipt)"
            >
              <div class="receipt-thumbnail"><i class="icon fas fa-receipt"></i></div>
              <div class="receipt-meta">
                <div class="merchant">{{ displayMerchant(receipt) }}</div>
                <div class="receipt-date">{{ formatDate(displayDate(receipt)) }}</div>
              </div>
              <div class="amount">
                <v-money v-if="receipt.totalAmount != null">{{ receipt.totalAmount }}</v-money>
                <span v-else>--</span>
              </div>
            </div>

            <div v-if="totalPages > 1" class="pagination-controls">
              <button
                v-for="p in totalPages"
                :key="p"
                @click.stop="goToPage(p)"
                :class="{ active: currentPage === p }"
                class="page-btn"
                type="button"
              >
                {{ p }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ed-modal>
</template>

<script>
import featureFlags from '@/core/feature-flags/feature-flags'
import receiptScansApi from '@/core/api/receiptScans'

export default {
  name: 'ReceiptFolderPicker',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    isProcessing: {
      type: Boolean,
      default: false
    },
    // When the host supplies the transaction being reconciled, the folder search is
    // ranked by match score against it (Platform auto-orders the result set). Desktop
    // callers omit it (default null) so the body param is absent and ordering is unchanged.
    transactionId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      featureFlags: featureFlags,
      receipts: [],
      isLoading: false,
      selectedReceiptId: null,
      currentPage: 1,
      pageSize: 25,
      totalPages: 1,
      isExpanded: false
    }
  },

  directives: {
    clickOutside: {
      bind (el, binding) {
        el.__vueClickOutside__ = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.__vueClickOutside__)
      },
      unbind (el) {
        document.removeEventListener('click', el.__vueClickOutside__)
        delete el.__vueClickOutside__
      }
    }
  },

  computed: {
    moment () {
      return global.moment
    },
    selectedReceipt () {
      if (!this.selectedReceiptId || !this.receipts) return null
      return this.receipts.find(r => r.receiptScanId === this.selectedReceiptId) || null
    }
  },

  watch: {
    show (newVal) {
      if (newVal) {
        this.selectedReceiptId = null
        this.currentPage = 1
        this.isExpanded = false
        this.fetchReceipts()
      } else {
        this.isExpanded = false
      }
    }
  },

  methods: {
    displayMerchant (receipt) {
      return (receipt && (receipt.merchantName || receipt.originalFileName)) || 'Receipt'
    },

    // Photon-extracted receipt date when available, otherwise the upload date (PRODUCTDEV-5783).
    displayDate (receipt) {
      return receipt && (receipt.receiptDate || receipt.createdAt)
    },

    formatDate (dateStr) {
      if (!dateStr) return 'N/A'
      var fmt = (this.$user && this.$user.sessionSettings &&
                 this.$user.sessionSettings.DateAndTimeFormats &&
                 this.$user.sessionSettings.DateAndTimeFormats.MomentDateFormat) || 'MM-DD-YYYY'
      try {
        return this.moment(dateStr).format(fmt)
      } catch (e) {
        return dateStr
      }
    },

    // The folder only ever lists the current user's own receipts, so every row is selectable.
    canSelectReceipt (receipt) {
      return !!receipt
    },

    togglePanel () {
      this.isExpanded = !this.isExpanded
    },

    closePanel () {
      this.isExpanded = false
    },

    onRowClick (receipt) {
      if (!this.canSelectReceipt(receipt)) return
      this.selectedReceiptId = receipt.receiptScanId
      this.isExpanded = false
    },

    async fetchReceipts () {
      this.isLoading = true
      try {
        // The folder is scoped server-side to the current user's own receipts
        // (PRODUCTDEV-5783) — no admin widening, no mineOnly flag needed.
        // Status is a single string (server SearchReceiptsQuery.Status is `string?`).
        // When the host passes transactionId, Platform ranks the result set by match
        // score against that transaction. Null (desktop default) → searchReceipts omits
        // the param via its `!= null` guard, so ordering is unchanged.
        var result = await receiptScansApi.searchReceipts(
          { status: 'FolderSaved', matchAgainstLegacyTransactionId: this.transactionId },
          this.currentPage,
          this.pageSize
        )
        this.receipts = result.items || []
        var totalCount = result.totalCount || 0
        this.totalPages = Math.ceil(totalCount / this.pageSize) || 1
      } catch (error) {
        console.error('Failed to load folder receipts:', error)
        this.$notifications.notify({
          type: 'danger',
          message: 'Failed to load receipts',
          placement: { from: 'top', align: 'center' }
        })
        this.receipts = []
      } finally {
        this.isLoading = false
      }
    },

    goToPage (page) {
      this.currentPage = page
      this.selectedReceiptId = null
      this.fetchReceipts()
    },

    handleAdd () {
      if (!this.selectedReceiptId || this.isProcessing) return
      // Pass the folder row's own metadata so the reconcile chip has the real file size,
      // name and type even when the S3 byte-fetch is unavailable (PRODUCTDEV-5783 — the
      // chip previously showed 0 bytes whenever that fetch was blocked).
      var r = this.selectedReceipt
      this.$emit('select', {
        receiptScanId: this.selectedReceiptId,
        sizeBytes: r ? r.sizeBytes : null,
        originalFileName: r ? r.originalFileName : null,
        contentType: r ? r.contentType : null
      })
    },

    handleClose () {
      this.selectedReceiptId = null
      this.isExpanded = false
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@/assets/sass/_variables.scss";

.receipt-folder-picker-body {
  padding: 12px 0;
  min-height: 200px;
}

.picker-subtitle {
  margin: 0 0 16px;
  color: #555;
  font-size: 14px;
  line-height: 1.4;
}

.loading-state,
.empty-state,
.feature-disabled {
  text-align: center;
  padding: 2rem;
  color: #666;

  p {
    margin: 0;
    font-size: 14px;
  }
}

.receipt-picker {
  display: flex;
  flex-direction: column;
}

.receipt-label {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 6px;
  color: #333;
}

.receipt-dropdown {
  position: relative;
}

.receipt-dropdown-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: #fff;
  padding: 8px 12px;
  text-align: left;
  cursor: pointer;
  min-height: 56px;
  transition: border-color 0.15s ease;

  &:hover,
  &:focus {
    border-color: #4a90e2;
    outline: none;
  }

  &.is-expanded {
    border-color: #4a90e2;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.receipt-thumbnail-trigger {
  width: 36px;
  height: 36px;

  .icon {
    font-size: 18px;
  }
}

.receipt-meta-trigger {
  flex: 1;
  min-width: 0;
}

.amount-trigger {
  margin-top: 0;
  font-weight: 600;
}

.receipt-dropdown-placeholder {
  flex: 1;
  color: #999;
  font-size: 14px;
}

.receipt-dropdown-caret {
  flex-shrink: 0;
  color: #888;
  font-size: 12px;
}

.receipt-dropdown-panel {
  position: absolute;
  top: calc(100% - 1px);
  left: 0;
  right: 0;
  border: 1px solid #4a90e2;
  border-top: none;
  background: #fff;
  border-radius: 0 0 3px 3px;
  max-height: 360px;
  overflow-y: auto;
  z-index: 50;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.06);
}

.receipt-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f9f9f9;
  }

  &.is-selected {
    background-color: #e6f2ff;
  }

  &.is-disabled {
    opacity: 0.55;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }
}

.receipt-thumbnail {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: linear-gradient(315deg, #8c407b 0%, #63417e 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  .icon {
    color: #fff;
    font-size: 22px;
  }
}

.receipt-meta {
  flex: 1;
  min-width: 0;

  .merchant {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .receipt-date {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
  }
}

.amount {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  text-align: right;
  margin-left: 8px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  border-top: 1px solid #eee;

  .page-btn {
    padding: 0.4rem 0.7rem;
    border: 1px solid #ddd;
    background: #fff;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      border-color: #4a90e2;
      color: #4a90e2;
    }

    &.active {
      background-color: #4a90e2;
      color: #fff;
      border-color: #4a90e2;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-transactions/ReceiptFolderPicker.vue